import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AgoraRTC from "agora-rtc-sdk-ng";
import { getBaseUrl } from "../utils";

const baseUrl = getBaseUrl();
const APP_ID = "35e6d26c9b11419e9d1493f857c3e81c";

const fetchUserData = async (channelName, setUserData) => {
    try {
        const response = await fetch(`${baseUrl}user_api/get_details_by_channel_id?channelId=${channelName}`);
        const data = await response.json();
        if (data.status) {
            setUserData({
                localUser: data.results.userDate,
                remoteUser: data.results.adminDate,
            });
        }
    } catch (error) {
        console.error("Error fetching user data:", error);
    }
};

const fetchToken = async (channelName) => {
    try {
        const response = await fetch(`${baseUrl}user_api/agora_token?channel=${channelName}`);
        const data = await response.json();
        if (!data.token || !data.uid) {
            throw new Error("Invalid token received from server.");
        }
        return data;
    } catch (error) {
        console.error("Error fetching Agora token:", error);
        return null;
    }
};

const AgoraAudioCallPage = () => {
    const { channelName } = useParams();
    const navigate = useNavigate();

    const clientRef = useRef(null);
    const localAudioTrackRef = useRef(null);
    const [userData, setUserData] = useState({ localUser: null, remoteUser: null });
    const [isAudioMuted, setIsAudioMuted] = useState(false);
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const initAgora = async () => {
            if (!channelName || clientRef.current) return;
            const fetchedTokenData = await fetchToken(channelName);
            if (!fetchedTokenData || !isMounted) return;

            const { token, uid } = fetchedTokenData;
            const agoraClient = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
            clientRef.current = agoraClient;

            try {
                await agoraClient.join(APP_ID, channelName, token, uid);
                if (!isMounted) return;
                setIsConnected(true);

                const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
                localAudioTrackRef.current = localAudioTrack;
                await agoraClient.publish([localAudioTrack]);

                agoraClient.on("user-published", async (user, mediaType) => {
                    if (mediaType === "audio") {
                        await agoraClient.subscribe(user, mediaType);
                        user.audioTrack.play();
                    }
                });

                fetchUserData(channelName, setUserData);
            } catch (error) {
                console.error("Agora Error:", error);
                navigate(-1);
            }
        };

        initAgora();

        return () => {
            isMounted = false;
            if (clientRef.current) {
                clientRef.current.leave();
                localAudioTrackRef.current?.stop();
                localAudioTrackRef.current?.close();
                clientRef.current = null;
            }
        };
    }, [channelName]);

    const handleMuteAudio = () => {
        if (localAudioTrackRef.current) {
            localAudioTrackRef.current.setMuted(!isAudioMuted);
            setIsAudioMuted(!isAudioMuted);
        }
    };

    const handleLeaveCall = async () => {
        if (clientRef.current) {
            await clientRef.current.leave();
            localAudioTrackRef.current?.stop();
            localAudioTrackRef.current?.close();
            clientRef.current = null;
        }
        navigate(-1);
    };

    return (
        <div className="w-full h-screen flex flex-col items-center justify-center bg-gray-900 text-white relative">
            <h2 className="text-2xl font-bold mb-4">Agora Audio Call</h2>

            <div className="w-full h-full flex flex-col items-center justify-center">
                {userData.remoteUser ? (
                    <div className="flex flex-col items-center">
                        <img src={userData.remoteUser.profile_img} alt={userData.remoteUser.name} className="w-32 h-32 rounded-full border-2 border-white" />
                        <span className="text-lg mt-2">{userData.remoteUser.name}</span>
                    </div>
                ) : (
                    <div className="text-gray-500">Waiting for remote user...</div>
                )}

                <div className="absolute bottom-4 right-4 flex flex-col items-center">
                    {userData.localUser && (
                        <>
                            <img src={userData.localUser.profile_img} alt={userData.localUser.name} className="w-24 h-24 rounded-full border-2 border-white" />
                            <span className="text-sm mt-1">{userData.localUser.name}</span>
                        </>
                    )}
                </div>
            </div>

            <div className="absolute bottom-4 flex gap-4">
                <button className="px-4 py-2 bg-blue-500 text-white rounded-lg" onClick={handleMuteAudio}>
                    {isAudioMuted ? "Unmute" : "Mute"}
                </button>
                <button className="px-6 py-2 bg-red-500 text-white rounded-lg" onClick={handleLeaveCall}>
                    End Call
                </button>
            </div>
        </div>
    );
};

export default AgoraAudioCallPage;